import { useWorkspaceChrome } from '@/components/linear/workspace-chrome';
import PriorityIcon from '@/components/linear/priority-icon';
import StatusIcon from '@/components/linear/status-icon';
import {
  agentsPath,
  cyclesPath,
  documentsPath,
  draftsPath,
  inboxPath,
  issuesPath,
  myIssuesPath,
  openIssuePath,
  projectPath,
  projectsPath,
  pulsePath,
  reviewsPath,
  searchPath,
  settingsPath,
  teamsPath,
  viewsPath,
} from '@/lib/linear-paths';
import type { Issue } from '@/types/issue';
import { router, usePage } from '@inertiajs/react';
import {
  Activity,
  BookOpen,
  Bot,
  FileText,
  Folder,
  GitPullRequest,
  Hexagon,
  Inbox,
  LayoutGrid,
  Plus,
  Receipt,
  Settings,
  User,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface PaletteIssue {
  id: string;
  identifier: string;
  title: string;
  status: Issue['status'];
  priority: Issue['priority'] | number;
}

interface PaletteProject {
  id: string;
  name: string;
}

interface PaletteItem {
  id: string;
  group: string;
  label: string;
  hint?: string;
  href?: string;
  action?: () => void;
  issue?: PaletteIssue;
}

export default function CommandPalette() {
  const { paletteOpen, setPaletteOpen, setCreateIssueOpen } = useWorkspaceChrome();
  const page = usePage<{
    issues?: PaletteIssue[];
    projects?: PaletteProject[];
  }>();
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const [remoteIssues, setRemoteIssues] = useState<PaletteIssue[]>([]);
  const [remoteProjects, setRemoteProjects] = useState<PaletteProject[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const q = query.trim();
    if (!paletteOpen || q.length < 2) {
      setRemoteIssues([]);
      setRemoteProjects([]);
      return;
    }

    const abort = new window.AbortController();
    fetch(`${searchPath()}.json?q=${encodeURIComponent(q)}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      signal: abort.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error('Search failed');
        return response.json() as Promise<unknown>;
      })
      .then((payload) => {
        const result = parseSearchResponse(payload);
        setRemoteIssues(result.issues);
        setRemoteProjects(result.projects);
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setRemoteIssues([]);
        setRemoteProjects([]);
      });

    return () => abort.abort();
  }, [paletteOpen, query]);

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const searching = q.length >= 2;
    const actions: PaletteItem[] = [
      {
        id: 'create',
        group: 'Actions',
        label: 'Create new issue',
        hint: 'C',
        action: () => {
          setPaletteOpen(false);
          setCreateIssueOpen(true);
        },
      },
      { id: 'inbox', group: 'Navigation', label: 'Go to Inbox', hint: 'G I', href: inboxPath() },
      {
        id: 'mine',
        group: 'Navigation',
        label: 'Go to My issues',
        hint: 'G M',
        href: myIssuesPath(),
      },
      { id: 'issues', group: 'Navigation', label: 'Go to Issues', hint: 'G E', href: issuesPath() },
      { id: 'pulse', group: 'Navigation', label: 'Go to Pulse', href: pulsePath() },
      { id: 'drafts', group: 'Navigation', label: 'Go to Drafts', href: draftsPath() },
      {
        id: 'reviews',
        group: 'Navigation',
        label: 'Go to Reviews',
        hint: 'G R',
        href: reviewsPath(),
      },
      { id: 'agents', group: 'Navigation', label: 'Go to Agents', href: agentsPath() },
      {
        id: 'projects',
        group: 'Navigation',
        label: 'Go to Projects',
        hint: 'G P',
        href: projectsPath(),
      },
      { id: 'cycles', group: 'Navigation', label: 'Go to Cycles', hint: 'G C', href: cyclesPath() },
      { id: 'views', group: 'Navigation', label: 'Go to Views', href: viewsPath() },
      {
        id: 'documents',
        group: 'Navigation',
        label: 'Go to Documents',
        hint: 'G D',
        href: documentsPath(),
      },
      { id: 'invoices', group: 'Navigation', label: 'Invoices', href: '/app/invoices' },
      { id: 'teams', group: 'Navigation', label: 'Go to Teams', hint: 'G T', href: teamsPath() },
      {
        id: 'settings',
        group: 'Navigation',
        label: 'Go to Settings',
        hint: 'G S',
        href: settingsPath(),
      },
    ];

    const issueSource = searching ? remoteIssues : (page.props.issues ?? []);
    const projectSource = searching ? remoteProjects : (page.props.projects ?? []);

    const issues: PaletteItem[] = issueSource.map((issue) => ({
      id: `issue-${issue.id}`,
      group: 'Issues',
      label: `${issue.identifier}  ${issue.title}`,
      href: openIssuePath(issue),
      issue,
    }));

    const projects: PaletteItem[] = projectSource.map((project) => ({
      id: `project-${project.id}`,
      group: 'Projects',
      label: project.name,
      href: projectPath(project.id),
    }));

    return [...actions, ...issues, ...projects].filter((item) => {
      if (!q) return true;
      if (searching && (item.group === 'Issues' || item.group === 'Projects')) return true;
      return (
        item.label.toLowerCase().includes(q) ||
        (item.issue?.identifier.toLowerCase().includes(q) ?? false)
      );
    });
  }, [
    page.props.issues,
    page.props.projects,
    query,
    remoteIssues,
    remoteProjects,
    setCreateIssueOpen,
    setPaletteOpen,
  ]);

  useEffect(() => {
    setIndex(0);
  }, [query, paletteOpen]);

  useEffect(() => {
    if (paletteOpen) {
      setQuery('');
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [paletteOpen]);

  const run = useCallback(
    (item: PaletteItem) => {
      setPaletteOpen(false);
      if (item.action) {
        item.action();
        return;
      }
      if (item.href) router.visit(item.href);
    },
    [setPaletteOpen]
  );

  useEffect(() => {
    if (!paletteOpen) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setIndex((current) => (current + 1) % Math.max(items.length, 1));
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setIndex(
          (current) => (current - 1 + Math.max(items.length, 1)) % Math.max(items.length, 1)
        );
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const item = items[index];
        if (item) run(item);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, items, paletteOpen, run]);

  if (!paletteOpen) return null;

  const groups = Array.from(new Set(items.map((item) => item.group)));

  return (
    <div
      className="ln-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setPaletteOpen(false);
      }}
    >
      <div className="ln-palette">
        <input
          ref={inputRef}
          className="ln-palette-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Type a command or search…"
        />
        <div className="ln-palette-list">
          {items.length === 0 ? (
            <div className="ln-empty" style={{ padding: 24 }}>
              No results.
            </div>
          ) : (
            groups.map((group) => (
              <div key={group}>
                <div className="ln-palette-group">{group}</div>
                {items
                  .filter((item) => item.group === group)
                  .map((item) => {
                    const globalIndex = items.findIndex((candidate) => candidate.id === item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className="ln-popover-item"
                        data-active={globalIndex === index ? 'true' : 'false'}
                        onMouseEnter={() => setIndex(globalIndex)}
                        onClick={() => run(item)}
                      >
                        <PaletteIcon item={item} />
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.label}
                        </span>
                        {item.hint ? <span className="ln-kbd">{item.hint}</span> : null}
                      </button>
                    );
                  })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function PaletteIcon({ item }: { item: PaletteItem }) {
  if (item.issue) {
    return (
      <span style={{ display: 'inline-flex', gap: 6 }}>
        <PriorityIcon priority={item.issue.priority} />
        <StatusIcon status={item.issue.status} />
      </span>
    );
  }
  if (item.id === 'create') return <Plus size={14} />;
  if (item.id === 'inbox') return <Inbox size={14} />;
  if (item.id === 'mine') return <User size={14} />;
  if (item.id === 'pulse') return <Activity size={14} />;
  if (item.id === 'drafts') return <FileText size={14} />;
  if (item.id === 'reviews') return <GitPullRequest size={14} />;
  if (item.id === 'agents') return <Bot size={14} />;
  if (item.id === 'projects' || item.group === 'Projects') return <Folder size={14} />;
  if (item.id === 'cycles') return <Hexagon size={14} />;
  if (item.id === 'views') return <LayoutGrid size={14} />;
  if (item.id === 'documents') return <BookOpen size={14} />;
  if (item.id === 'invoices') return <Receipt size={14} />;
  if (item.id === 'teams') return <Users size={14} />;
  if (item.id === 'settings') return <Settings size={14} />;
  return <Inbox size={14} />;
}

function parseSearchResponse(value: unknown): {
  issues: PaletteIssue[];
  projects: PaletteProject[];
} {
  if (typeof value !== 'object' || value === null) {
    return { issues: [], projects: [] };
  }

  const record = value as Record<string, unknown>;

  return {
    issues: Array.isArray(record.issues) ? record.issues.filter(isPaletteIssue) : [],
    projects: Array.isArray(record.projects) ? record.projects.filter(isPaletteProject) : [],
  };
}

function isPaletteIssue(value: unknown): value is PaletteIssue {
  if (typeof value !== 'object' || value === null) return false;

  const record = value as Record<string, unknown>;

  return (
    typeof record.id === 'string' &&
    typeof record.identifier === 'string' &&
    typeof record.title === 'string' &&
    typeof record.status === 'string' &&
    typeof record.priority === 'number'
  );
}

function isPaletteProject(value: unknown): value is PaletteProject {
  if (typeof value !== 'object' || value === null) return false;

  const record = value as Record<string, unknown>;

  return typeof record.id === 'string' && typeof record.name === 'string';
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}
